import { TFunction, useTranslation } from 'react-i18next';
import styles from './CreateGameStatsPage.module.less';
import {
	Col,
	Row,
	Form,
	Button,
	Radio,
	Space,
	RadioChangeEvent,
	message,
	Checkbox,
} from 'antd';
import FormInput from 'components/form/FormItem/FormInput';
import { useHistory, useParams } from 'react-router-dom';
import api, {
	CreateGameStatsRequest,
	GameStatFormat,
	UnitOfMeasurement,
} from 'api';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import OmniNotification from 'components/OmniNotification';
import { useSelector } from 'react-redux';
import { projectSelector } from 'redux/selectors/projectSelector';
import Breadcrumb, { IRoutes } from 'components/Breadcrumb/Breadcrumb';
import { debounce } from 'lodash';
import FormItem from 'components/form/FormItem';

type ParamsProps = {
	projectId: string;
};

const defaultValues: CreateGameStatsRequest = {
	apiName: '',
	title: '',
	format: GameStatFormat.Integer,
	unitOfMeasurement: UnitOfMeasurement.Inches,
	serverOnly: false,
};

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		apiName: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('createGameStatsPage.apiName.label'),
				}),
			)
			.max(
				1000,
				t('validation.maxLength', {
					fieldName: t('createGameStatsPage.apiName.label'),
					count: 1000,
				}),
			)
			.test(
				'valid-apiName-free',
				({ value }) =>
					t('createGameStatsPage.apiName.alreadyTaken', { name: value }),
				async (value, context) =>
					api.validApiName({
						apiName: value,
						projectId: defaultValues.projectId,
					}),
			)
			.matches(
				/^(?!.*[?;\/:@=&]).*$/,
				t('common.unsafeCharacters', {
					fieldName: t('createGameStatsPage.apiName.label'),
				}),
			)
			.matches(
				/^[\S]*$/,
				t('common.noSpaces', {
					fieldName: t('createGameStatsPage.apiName.label'),
				}),
			),
		title: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('createGameStatsPage.title.label'),
				}),
			)
			.max(
				80,
				t('validation.maxLength', {
					fieldName: t('createGameStatsPage.title.label'),
					count: 80,
				}),
			),
		serverOnly: yup.boolean().nullable(),
	});

export default function CreateGameStatsPage() {
	const { t } = useTranslation();
	const history = useHistory();
	const projectInfo = useSelector(projectSelector);
	const [routes, setRoutes] = useState<IRoutes[]>([]);
	const validationSchema: any = getValidationSchema(t);
	const [createLoading, setCreateLoading] = useState<boolean>(false);
	const [format, setFormat] = useState(0);
	const [unitOfMeasurement, setUnitOfMeasurement] = useState(0);
	defaultValues.projectId = Number.parseInt(useParams<ParamsProps>().projectId);
	const pageForm = useForm<CreateGameStatsRequest>({
		mode: 'onChange',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});
	const onSubmit = async (values: CreateGameStatsRequest) => {
		setCreateLoading(true);
		try {
			await api.createGameStat(values);
			setCreateLoading(false);
			OmniNotification.success({
				message: t('createGameStatsPage.notification.success.message'),
				description: t('createGameStatsPage.notification.success.description', {
					title: values.title,
				}),
			});
		} catch {
			setCreateLoading(false);
			OmniNotification.error({
				message: t('createGameStatsPage.notification.error.message'),
				description: t('createGameStatsPage.notification.error.description'),
			});
		}

		navigateToGameStatsIndex();
	};
	function navigateToGameStatsIndex() {
		history.push(`../game-stats`);
	}
	const onFormatChange = (e: RadioChangeEvent) => {
		pageForm.setValue(
			'format',
			Number.parseInt(e.target.value) as GameStatFormat,
		);
		setFormat(e.target.value);
	};
	const onUnitOfMeasurementChange = (e: RadioChangeEvent) => {
		pageForm.setValue(
			'unitOfMeasurement',
			Number.parseInt(e.target.value) as UnitOfMeasurement,
		);
		setUnitOfMeasurement(e.target.value);
	};

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: 'game-services',
				breadcrumbName: t('gameServicesPage.header'),
			},
			{
				path: 'game-stats',
				breadcrumbName: t('gameStatsPage.header'),
			},
			{
				path: '',
				breadcrumbName: t('createGameStatsPage.header'),
			},
		]);
	}, [projectInfo?.id, projectInfo?.name]);

	const sumbit = useCallback(
		debounce(
			() => {
				pageForm.handleSubmit(onSubmit)();
			},
			500,
			{ leading: true, trailing: false },
		),
		[],
	);

	return (
		<div className={styles.pageContainer}>
			<Breadcrumb routes={routes} />
			<div className="page-head">
				<div>
					<h2 className="h2" style={{ wordBreak: 'break-all' }}>
						{t('createGameStatsPage.header')}
					</h2>
				</div>
			</div>
			<FormProvider {...pageForm}>
				<Form data-testid="achievementForm" layout="vertical" onFinish={sumbit}>
					<Row gutter={16}>
						<Col span={16}>
							<Row gutter={24}>
								<Col span={12} data-testid="apiNameGroup">
									<FormInput
										control={pageForm.control}
										translationKey="createGameStatsPage.apiName"
									/>
								</Col>
								<Col span={12} data-testid="titleGroup">
									<FormInput
										control={pageForm.control}
										translationKey="createGameStatsPage.title"
									/>
								</Col>
							</Row>
						</Col>
					</Row>
					<Row gutter={24} className={styles.formRow}>
						<Col span={24}>
							<Row>{t('createGameStatsPage.format.label')}</Row>
							<Row className="ant-form-item-explain hint">
								{t('createGameStatsPage.format.hint')}
							</Row>
						</Col>
					</Row>
					<Row>
						<Col span={24} style={{ marginLeft: 20, marginTop: 4 }}>
							<Radio.Group
								name="format"
								onChange={onFormatChange}
								value={format}
							>
								<Space direction="vertical">
									<Radio
										data-testid="gameStatFormatInteger"
										value={GameStatFormat.Integer}
									>
										{t(`createGameStatsPage.format.gameStatFormatEnum.integer`)}
									</Radio>
									<Radio
										data-testid="gameStatFormatDecimal"
										value={GameStatFormat.Decimal}
									>
										{t(`createGameStatsPage.format.gameStatFormatEnum.decimal`)}
									</Radio>
									<Radio
										data-testid="gameStatFormatPercentage"
										value={GameStatFormat.Percentage}
									>
										{t(
											`createGameStatsPage.format.gameStatFormatEnum.percentage`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatFormatHourMinutesSeconds"
										value={GameStatFormat.HourMinutesSeconds}
									>
										{t(
											`createGameStatsPage.format.gameStatFormatEnum.hourMinutesSeconds`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatFormatDaysHoursMinutes"
										value={GameStatFormat.DaysHoursMinutes}
									>
										{t(
											`createGameStatsPage.format.gameStatFormatEnum.daysHoursMinutes`,
										)}
									</Radio>
								</Space>
							</Radio.Group>
						</Col>
					</Row>
					<Row gutter={24} className={styles.formRow}>
						<Col span={24}>
							<Row>{t('createGameStatsPage.unitOfMeasurement.label')}</Row>
							<Row className="ant-form-item-explain hint">
								{t('createGameStatsPage.unitOfMeasurement.hint')}
							</Row>
						</Col>
					</Row>
					<Row>
						<Col span={24} style={{ marginLeft: 20, marginTop: 4 }}>
							<Radio.Group
								name="unitOfMeasurement"
								onChange={onUnitOfMeasurementChange}
								value={unitOfMeasurement}
							>
								<Space direction="vertical">
									<Radio
										data-testid="gameStatUnitOfMeasurementInches"
										value={UnitOfMeasurement.Inches}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.inches`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementFeet"
										value={UnitOfMeasurement.Feet}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.feet`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementMiles"
										value={UnitOfMeasurement.Miles}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.miles`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementCentimeters"
										value={UnitOfMeasurement.Centimeters}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.centimeters`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementMeters"
										value={UnitOfMeasurement.Meters}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.meters`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementKilometers"
										value={UnitOfMeasurement.Kilometers}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.kilometers`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementMilliseconds"
										value={UnitOfMeasurement.Milliseconds}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.milliseconds`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementSeconds"
										value={UnitOfMeasurement.Seconds}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.seconds`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementMinutes"
										value={UnitOfMeasurement.Minutes}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.minutes`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementHours"
										value={UnitOfMeasurement.Hours}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.hours`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementDays"
										value={UnitOfMeasurement.Days}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.days`,
										)}
									</Radio>
									<Radio
										data-testid="gameStatUnitOfMeasurementNumbers"
										value={UnitOfMeasurement.Numbers}
									>
										{t(
											`createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.numbers`,
										)}
									</Radio>
								</Space>
							</Radio.Group>
						</Col>
					</Row>
					<Row gutter={24} className={styles.formRow}>
						<Col span={24}>
							<Row>{t('createGameStatsPage.serverOnly.label')}</Row>
							<Row>
								<FormItem
									name="serverOnly"
									control={pageForm.control}
									render={({ field }) => (
										<Checkbox
											data-testid="isServerOnlyCheckbox"
											checked={field.value}
											{...field}
										>
											{t('createGameStatsPage.serverOnly.hint')}
										</Checkbox>
									)}
								/>
							</Row>
						</Col>
					</Row>
					<Row gutter={24} style={{ marginTop: 24 }}>
						<Button
							className="ant-btn color-dark"
							data-testid="cancel"
							onClick={navigateToGameStatsIndex}
							loading={createLoading}
						>
							{t('common.cancel')}
						</Button>
						<Button
							className="ant-btn-primary"
							data-testid="submit"
							style={{ marginLeft: '8px' }}
							htmlType="submit"
							loading={createLoading}
						>
							{t('createGameStatsPage.create')}
						</Button>
					</Row>
				</Form>
			</FormProvider>
		</div>
	);
}
